// PowMixMulDivTask.js
// Miks: a^m · a^p : a^n  lub  a^m : a^n · a^p  = a^{m + p - n}
// Wersja bez ujemnych wykładników w treści i w wyniku (wykładniki naturalne)
// + losowa forma dzielenia (dwukropek / kreska ułamkowa) USTAWIANA w generateTask

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// przy exp = 1 pokazujemy samą podstawę (ukrycie ^1)
const factorLatex = (a, exp) => (exp === 1 ? `${a}` : `${a}^{${exp}}`);

// Pomocnik do dzielenia w przypadku „mnożenie potem dzielenie”:
// a^m · a^p : a^n  →  albo "\dfrac{a^m · a^p}{a^n}", albo "(a^m · a^p) : a^n"
// UWAGA: brak Math.random – przekazujemy useFraction z data
const divisionMulThenDivLatex = (a, m, p, n, useFraction) => {
  const numerator = `${factorLatex(a, m)}\\,\\cdot\\,${factorLatex(a, p)}`;
  const denominator = `${factorLatex(a, n)}`;

  if (useFraction) {
    return `\\dfrac{${numerator}}{${denominator}}`;
  }
  // przy dwukropku chcemy jasno, że dzielimy cały iloczyn w liczniku
  return `\\big(${numerator}\\big)\\,:\\,${denominator}`;
};

// Pomocnik do dzielenia w przypadku „dzielenie potem mnożenie”:
// a^m : a^n · a^p  →  albo "a^m : a^n", albo "\dfrac{a^m}{a^n}"
// (a potem dopiero "· a^p" dopinamy z zewnątrz)
// UWAGA: brak Math.random – przekazujemy useFraction z data
const divisionDivThenMulPrefixLatex = (a, m, n, useFraction) => {
  const numerator = `${factorLatex(a, m)}`;
  const denominator = `${factorLatex(a, n)}`;

  if (useFraction) {
    return `\\dfrac{${numerator}}{${denominator}}`;
  }
  return `${numerator}\\,:\\,${denominator}`;
};

export function generateTask() {
  const basePool = [2, 3, 4, 5, 6, 7, 8, 9];
  const a = pick(basePool);

  // Losujemy tak, aby m, p, n ∈ {1,...,9} oraz m + p - n ≥ 1 (wynik naturalny)
  const m = pick([1, 2, 3, 4, 5, 6, 7, 8, 9]);
  const p = pick([1, 2, 3, 4, 5, 6, 7, 8, 9]);

  // n dobieramy tak, by nie przekroczyć m + p (i ≤9)
  const maxN = Math.min(9, m + p);
  const n = pick(Array.from({ length: maxN }, (_, i) => i + 1));

  // Losowa kolejność: mnożenie→dzielenie albo dzielenie→mnożenie
  const order = pick(["mulThenDiv", "divThenMul"]);

  // TUTAJ losujemy formę zapisu dzielenia, zapisujemy w data
  const useFraction = Math.random() < 0.5;

  return { type: "PowMixMulDivTask", data: { a, m, p, n, order, useFraction } };
}

export function renderTask({ data }) {
  const { a, m, p, n, order, useFraction } = data;

  let leftLatex;

  if (order === "mulThenDiv") {
    // a^m · a^p : a^n
    leftLatex = divisionMulThenDivLatex(a, m, p, n, useFraction);
  } else {
    // a^m : a^n · a^p
    const firstPart = divisionDivThenMulPrefixLatex(a, m, n, useFraction);
    leftLatex = `${firstPart}\\,\\cdot\\,${factorLatex(a, p)}`;
  }

  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={`${a}`} />:
      </div>
      <BlockMath math={leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder="np. 5^3"
        label=""
        powerPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

// Parsowanie wpisu typu "2^7"
function parsePowerInput(raw) {
  if (!raw) return null;
  const match = raw.replace(/\s/g, "").match(/^(\d+)\^(\d+)$/);
  if (!match) return null;
  return { base: parseInt(match[1], 10), exp: parseInt(match[2], 10) };
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return false;
  const parsed = parsePowerInput(userAnswer.raw);
  if (!parsed) return false;
  const { a, m, p, n } = data;
  const target = m + p - n;
  return parsed.base === a && parsed.exp === target;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  const raw = String(userAnswer.raw).trim().replace(/\s+/g, "");
  let latex = raw;
  const match = raw.match(/^(\d+)\^(\d+)$/);
  if (match) latex = `${match[1]}^{${match[2]}}`;

  return <InlineMath math={latex} />;
}

export function renderAnswer({ data }) {
  const { a, m, p, n } = data;
  return <InlineMath math={`${a}^{${m + p - n}}`} />;
}

export function renderExplanation({ data }) {
  const { a, m, p, n, order } = data;

  // W wyjaśnieniu zostawiamy klasyczną, „symboliczną” wersję z dwukropkiem,
  // żeby reguła była czytelna, bez losowej kreski łamkowej.
  const leftLatex =
    order === "divThenMul"
      ? `${factorLatex(a, m)}\\,:\\,${factorLatex(a, n)}\\,\\cdot\\,${factorLatex(a, p)}`
      : `${factorLatex(a, m)}\\,\\cdot\\,${factorLatex(a, p)}\\,:\\,${factorLatex(a, n)}`;

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p>
        <strong>Krok 1:</strong> Zasady działań na potęgach o tej samej
        podstawie:
      </p>
      <BlockMath math={'a^{m}\\cdot a^{n}=a^{m+n}\\quad\\text{i}\\quad a^{m}:a^{n}=a^{m-n}'} />
      <p>
        <strong>Krok 2:</strong> Podstaw i sprowadź do jednej potęgi:
      </p>
      <BlockMath
        math={`${leftLatex}\\;=\\;${a}^{${m}+${p}-${n}}\\;=\\;${a}^{${m + p - n}}`}
      />
    </div>
  );
}
